/**
 * Applies windowing to a stream of requests, allowing for the merging of multiple requests into a single larger request.
 * The design assumes that the request can be symmetrically folded; so the resulting merged request has the same type as
 * an individual request.
 */ export class RequestBatcher {
    /**
     * Dispatches a single request to the windowing function.
     * If `requestWindowInMillis = 0` then no windowing is applied.
     * @param request the single request to dispatch
     */ dispatchRequest(request) {
        if (this.requestWindowInMillis === 0) return this.apiHandler(request);
        const pendingRequest = this._pending.find((pending)=>this.canRequestsBeMerged(pending.request, request)
        );
        if (pendingRequest) {
            pendingRequest.request = this.mergeRequests(pendingRequest.request, request);
            return pendingRequest.promise;
        }
        this._lastId = this._lastId < Number.MAX_SAFE_INTEGER ? this._lastId + 1 : 0;
        const id = this._lastId;
        const promise = new Promise((resolve, reject)=>{
            window.setTimeout(async ()=>{
                const deqeuedRequestIndex = this._pending.findIndex((p)=>p.id === id
                );
                if (deqeuedRequestIndex === -1) throw new Error(`Request ${id} cannot be found.`);
                const deqeuedRequest = this._pending.splice(deqeuedRequestIndex, 1)[0];
                await this.apiHandler(deqeuedRequest.request).then(resolve, reject);
            }, this.requestWindowInMillis);
        });
        this._pending.push({
            id,
            request,
            promise
        });
        return promise;
    }
    constructor(requestWindowInMillis, apiHandler, canRequestsBeMerged, mergeRequests){
        this.requestWindowInMillis = requestWindowInMillis;
        this.apiHandler = apiHandler;
        this.canRequestsBeMerged = canRequestsBeMerged;
        this.mergeRequests = mergeRequests;
        this._lastId = 0;
        this._pending = [];
        if (requestWindowInMillis < 0) {
            throw new Error("Argument 'requestWindowInMillis' cannot be negative.");
        }
    }
}
